import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { FileText, LogOut, Award, Users } from "lucide-react";
import IncidentReportPage from "./IncidentReportPage";
import SpecialistGroupsPage from "./SpecialistGroupsPage";

export default function SpecialistDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showIncidentReport, setShowIncidentReport] = useState(false);
  const [showGroups, setShowGroups] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  if (showIncidentReport) {
    return (
      <IncidentReportPage
        group={{ code: "SPECIALIST", name: "Specialist" }}
        mode="specialist"
        onBack={() => setShowIncidentReport(false)}
      />
    );
  }

  if (showGroups) {
    return <SpecialistGroupsPage onBack={() => setShowGroups(false)} />;
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Award className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h1 className="text-base font-bold text-emerald-800">Specialist Portal</h1>
              <p className="text-xs text-muted-foreground">{user?.group_name || "Specialist"}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="specialist-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-extrabold text-gray-900">Welcome, Specialist</h2>
          <p className="text-sm text-muted-foreground mt-1">
            File an incident report for any camper. The supervisor team is notified instantly.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Card
            className="border-2 border-emerald-300 hover:border-emerald-500 hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-white to-emerald-50"
            onClick={() => setShowIncidentReport(true)}
            data-testid="specialist-card-incident-report"
          >
            <CardContent className="p-6 flex flex-col items-center gap-3 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-emerald-700" />
              </div>
              <h3 className="text-lg font-bold text-emerald-900">Incident Report</h3>
              <p className="text-xs text-emerald-800">
                Pick the division, group, and camper. Enter your specialty and submit.
              </p>
              <Button
                size="sm"
                className="mt-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="specialist-open-incident-btn"
              >
                Open Form
              </Button>
            </CardContent>
          </Card>

          <Card
            className="border-2 border-sky-300 hover:border-sky-500 hover:shadow-xl transition-all cursor-pointer bg-gradient-to-br from-white to-sky-50"
            onClick={() => setShowGroups(true)}
            data-testid="specialist-card-groups"
          >
            <CardContent className="p-6 flex flex-col items-center gap-3 text-center">
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center">
                <Users className="w-8 h-8 text-sky-700" />
              </div>
              <h3 className="text-lg font-bold text-sky-900">Groups</h3>
              <p className="text-xs text-sky-800">
                Total kids per group (excluding CIT). Tap in to see every camper.
              </p>
              <Button
                size="sm"
                className="mt-1 bg-sky-600 hover:bg-sky-700 text-white"
                data-testid="specialist-open-groups-btn"
              >
                View Groups
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
